import { cacheFn } from '@shared-components-web/utils/cacheFn/cacheFn';
import { getGraphHeaders } from '@shared-components-web/utils/getGraphHeaders';
import { logWarning } from '@shared-components-web/utils/miscUtils';
import { BffBrandType } from '@src/lib/__generated/beffe/sdk';

const BFF_NEARBY_FACILITIES_BY_LOCATION_QUERY = `
query bffNearByFacilitiesByLocation(
	$brand: BffBrandType!
	$latitude: Float!
	$longitude: Float!
	$fetchAllIfNoResults: Boolean
	$radiusMiles: Int
	$maxItems: Int
) {
	facilityByLocation(
		brand: $brand
		latitude: $latitude
		longitude: $longitude
		fetchAllIfNoResults: $fetchAllIfNoResults
		radiusMiles: $radiusMiles
		maxItems: $maxItems
	) {
		name
		id
		code
		brand
		phoneNumber
		scheduleAppointmentURL
		schedulingSystem
		revspringAccountId
		revspringLocationValue
		address {
			address1
			address2
			city
			stateCode
			zipCode
		}
		location {
			distance
			landmarks
			latitude
			longitude
			neighboringAreas
			timeZone
		}
		workingHours {
			date
			open {
				from
				to
			}
		}
		status
		comingSoon
		openDate
		attributes {
			name
			value
		}
		email
	}
}
`;

export const getBffNearByFacilitiesByLocation = cacheFn(
	async (
		brand: BffBrandType,
		latitude: number,
		longitude: number,
		url: string,
		appName: string,
		appVersion: string,
		additionalParams?: {
			radiusMiles?: number;
			maxItems?: number;
			fetchAllIfNoResults?: boolean;
		}
	) => {
		try {
			if (!brand) {
				return null;
			}
			if (!latitude || !longitude) {
				return Promise.reject(`Invalid coordinates of latitude: ${latitude} and longitude: ${longitude}`);
			}

			const { getBeffeGraphQlClient } = await import('@src/lib/client');
			const response = await getBeffeGraphQlClient(url)
				.query<{ facilityByLocation: unknown[] }>(
					BFF_NEARBY_FACILITIES_BY_LOCATION_QUERY,
					{
						brand: brand as never,
						latitude,
						longitude,
						fetchAllIfNoResults: !!additionalParams?.fetchAllIfNoResults,
						radiusMiles: additionalParams?.radiusMiles ?? 50,
						maxItems: additionalParams?.maxItems ?? 1000,
					},
					{ fetchOptions: { headers: getGraphHeaders(appName, appVersion) } }
				)
				.toPromise();

			return response.data;
		} catch {
			logWarning(`Could not find any near by facilities data for: ${brand}`);
		}
	}
);
